import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { recorrenciasApi } from '../../api/endpoints'
import { Button, IconButton } from '../../components/ui/Button'
import { Input, Toggle } from '../../components/ui/Input'
import { Card, CardHeader, KpiCard } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { LoadingState, ErrorState, EmptyState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { Table, THead, Th, Tr, Td } from '../../components/ui/Table'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { TransactionStatus, TRANSACTION_STATUS_LABEL, TransactionType } from '../../lib/enums'
import { formatMoney, formatDate } from '../../lib/utils'
import type { OcorrenciaRecorrencia } from '../../types'

export function RecorrenciaDetalhePage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()

  const [adiantando, setAdiantando] = useState(false)
  const [renegociando, setRenegociando] = useState(false)
  const [verHistorico, setVerHistorico] = useState(false)
  const [verQuebra, setVerQuebra] = useState<OcorrenciaRecorrencia | null>(null)

  const { data: r, isLoading, isError } = useQuery({
    queryKey: ['recorrencias', id],
    queryFn: () => recorrenciasApi.obter(id),
    enabled: !!id,
  })
  const { data: ocorrencias = [], isLoading: carregandoOcorrencias } = useQuery({
    queryKey: ['recorrencias', id, 'ocorrencias'],
    queryFn: () => recorrenciasApi.listarOcorrencias(id),
    enabled: !!id,
  })

  function invalidar() {
    qc.invalidateQueries({ queryKey: ['recorrencias', id] })
    qc.invalidateQueries({ queryKey: ['recorrencias', id, 'ocorrencias'] })
    qc.invalidateQueries({ queryKey: ['recorrencias'] })
    qc.invalidateQueries({ queryKey: ['transacoes'] })
  }

  const projetar = useMutation({
    mutationFn: () => recorrenciasApi.projetar(id),
    onSuccess: (qtd) => { invalidar(); toast.success(`${qtd} ocorrência(s) projetada(s) até o fim.`) },
    onError: (e: Error) => toast.error(e.message),
  })

  const toggleUma = useMutation({
    mutationFn: ({ transacaoId, gerarLancamento }: { transacaoId: string; gerarLancamento: boolean }) =>
      recorrenciasApi.toggleLancamentoOcorrencia(id, transacaoId, gerarLancamento),
    onSuccess: () => invalidar(),
    onError: (e: Error) => toast.error(e.message),
  })

  const toggleTodas = useMutation({
    mutationFn: (gerarLancamento: boolean) => recorrenciasApi.toggleLancamentoEmMassa(id, gerarLancamento),
    onSuccess: () => { invalidar(); toast.success('Ocorrências atualizadas.') },
    onError: (e: Error) => toast.error(e.message),
  })

  if (isLoading) return <LoadingState />
  if (isError || !r) return <ErrorState message="Recorrência não encontrada." />

  const reais = ocorrencias.filter((o) => !o.projetada)
  const todasGeramLancamento = reais.length > 0 && reais.every((o) => o.gerarLancamento)
  const pendentes = ocorrencias.filter((o) => !o.projetada && o.status === TransactionStatus.Pending)
  const valorRestante = pendentes.reduce((soma, o) => soma + o.valor, 0)
  const temProjetaveis = ocorrencias.some((o) => o.projetada)

  return (
    <>
      <PageHeader title={r.description} />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <IconButton icon="ri-arrow-left-line" label="Voltar" tone="muted" onClick={() => navigate('/recorrencias')} />
          <Badge variant={r.ativa ? 'income' : 'muted'}>{r.ativa ? 'Ativa' : 'Inativa'}</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          {r.dataFim && temProjetaveis && (
            <Button variant="soft-info" size="sm" icon="ri-calendar-2-line" loading={projetar.isPending} onClick={() => projetar.mutate()}>
              Projetar até o fim
            </Button>
          )}
          {r.dataFim && pendentes.length > 0 && (
            <Button variant="soft-primary" size="sm" icon="ri-flashlight-line" onClick={() => setAdiantando(true)}>Pagamento antecipado</Button>
          )}
          {r.dataFim && pendentes.length > 0 && (
            <Button variant="soft-danger" size="sm" icon="ri-file-edit-line" onClick={() => setRenegociando(true)}>Renegociar</Button>
          )}
          <Button variant="light" size="sm" icon="ri-history-line" onClick={() => setVerHistorico(true)}>Histórico de renegociações</Button>
        </div>
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard title="Valor por parcela" value={formatMoney(r.amount)} icon="ri-money-dollar-circle-line" tone="primary" />
        <KpiCard title="Valor restante" value={formatMoney(valorRestante)} icon="ri-hourglass-line" tone="warning"
          footer={<p className="mt-3 text-2xs text-muted">{pendentes.length} parcela(s) pendente(s){r.quantidadeParcelas ? ` de ${r.quantidadeParcelas}` : ''}</p>} />
        <KpiCard title="Próxima ocorrência" value={r.ativa ? formatDate(r.proximaOcorrencia) : '—'} icon="ri-calendar-event-line" tone="info" />
      </div>

      <Card>
        <CardHeader
          title="Ocorrências"
          icon="ri-list-check-2"
          actions={
            reais.length > 0 && (
              <label className="flex items-center gap-2 text-13 text-muted">
                Gerar lançamento (todas)
                <Toggle checked={todasGeramLancamento} onChange={() => toggleTodas.mutate(!todasGeramLancamento)} disabled={toggleTodas.isPending} />
              </label>
            )
          }
        />
        {carregandoOcorrencias ? (
          <LoadingState />
        ) : ocorrencias.length === 0 ? (
          <EmptyState icon="ri-list-check-2" message="Nenhuma ocorrência ainda." />
        ) : (
          <Table>
            <THead>
              <tr>
                <Th>#</Th><Th>Data</Th><Th>Status</Th><Th className="text-right">Bruto</Th>
                {r.type === TransactionType.Income && <Th className="text-right">Líquido esperado</Th>}
                <Th>Lançamento</Th>
              </tr>
            </THead>
            <tbody>
              {ocorrencias.map((o, i) => (
                <Tr key={o.id ?? `projetada-${i}`}>
                  <Td>{o.numero ? `${o.numero}${o.total ? ` de ${o.total}` : ''}` : '—'}</Td>
                  <Td className="whitespace-nowrap">{formatDate(o.data)}</Td>
                  <Td>
                    {o.projetada ? (
                      <Badge variant="muted">Previsto</Badge>
                    ) : (
                      <Badge variant={o.status === TransactionStatus.Confirmed ? 'income' : o.status === TransactionStatus.Cancelled ? 'expense' : 'muted'}>
                        {o.status != null ? TRANSACTION_STATUS_LABEL[o.status] : '—'}
                      </Badge>
                    )}
                  </Td>
                  <Td className="whitespace-nowrap text-right text-white">{formatMoney(o.valor)}</Td>
                  {r.type === TransactionType.Income && (
                    <Td className="whitespace-nowrap text-right">
                      {o.ajustes && o.ajustes.length > 0 ? (
                        <button type="button" className="font-medium text-primary hover:underline" onClick={() => setVerQuebra(o)}>
                          {formatMoney(o.valorLiquidoEsperado ?? o.valor)} <i className="ri-information-line" aria-hidden />
                        </button>
                      ) : (
                        <span className="text-white">{formatMoney(o.valorLiquidoEsperado ?? o.valor)}</span>
                      )}
                    </Td>
                  )}
                  <Td>
                    {o.id ? (
                      <Toggle
                        checked={o.gerarLancamento}
                        onChange={() => toggleUma.mutate({ transacaoId: o.id!, gerarLancamento: !o.gerarLancamento })}
                        disabled={toggleUma.isPending}
                      />
                    ) : (
                      <span className="text-2xs text-muted">—</span>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {adiantando && (
        <AdiantarPagamentoModal
          recorrenciaId={id} pendentes={pendentes} descricao={r.description}
          onClose={() => setAdiantando(false)} onAplicado={invalidar}
        />
      )}
      {renegociando && (
        <RenegociarModal
          recorrenciaId={id} valorParcelaAtual={r.amount} valorRestante={valorRestante} quantidadePendente={pendentes.length}
          onClose={() => setRenegociando(false)} onAplicado={invalidar}
        />
      )}
      {verHistorico && <HistoricoRenegociacoesModal recorrenciaId={id} onClose={() => setVerHistorico(false)} />}
      {verQuebra && <QuebraLiquidoModal ocorrencia={verQuebra} onClose={() => setVerQuebra(null)} />}
    </>
  )
}

function QuebraLiquidoModal({ ocorrencia, onClose }: { ocorrencia: OcorrenciaRecorrencia; onClose: () => void }) {
  const liquido = ocorrencia.valorLiquidoEsperado ?? ocorrencia.valor
  return (
    <Modal open onClose={onClose} title={`Líquido esperado · ${formatDate(ocorrencia.data)}`} size="sm">
      <div className="space-y-3">
        <div className="flex items-center justify-between text-13">
          <span className="text-muted">Bruto</span>
          <span className="font-medium text-white">{formatMoney(ocorrencia.valor)}</span>
        </div>
        {(ocorrencia.ajustes ?? []).map((item, i) => (
          <div key={i} className="flex items-center justify-between text-13">
            <span className="text-muted">{item.descricao}</span>
            <span className={item.valor < 0 ? 'text-expense' : 'text-income'}>
              {item.valor < 0 ? '' : '+'}{formatMoney(item.valor)}
            </span>
          </div>
        ))}
        <div className="flex items-center justify-between border-t border-border/60 pt-3 text-13">
          <span className="font-semibold text-white">Líquido esperado</span>
          <span className="font-semibold text-white">{formatMoney(liquido)}</span>
        </div>
      </div>
    </Modal>
  )
}

function AdiantarPagamentoModal({ recorrenciaId, pendentes, descricao, onClose, onAplicado }: {
  recorrenciaId: string; pendentes: OcorrenciaRecorrencia[]; descricao: string; onClose: () => void; onAplicado: () => void
}) {
  const toast = useToast()
  const numeros = pendentes.map((p) => p.numero).filter((n): n is number => n != null)
  const minNumero = numeros.length ? Math.min(...numeros) : undefined
  const maxNumero = numeros.length ? Math.max(...numeros) : undefined

  const [desdeNumero, setDesdeNumero] = useState(minNumero ? String(minNumero) : '')
  const [ateNumero, setAteNumero] = useState(maxNumero ? String(maxNumero) : '')
  const [valorPago, setValorPago] = useState('')
  const [data, setData] = useState(new Date().toISOString().slice(0, 10))
  const [erro, setErro] = useState<string | null>(null)

  const selecionadas = pendentes.filter((p) =>
    p.numero != null && p.numero >= Number(desdeNumero || minNumero) && p.numero <= Number(ateNumero || maxNumero))
  const valorNominal = selecionadas.reduce((s, p) => s + p.valor, 0)

  const adiantar = useMutation({
    mutationFn: () => recorrenciasApi.adiantarOcorrencias(recorrenciaId, {
      desdeNumero: desdeNumero ? Number(desdeNumero) : undefined,
      ateNumero: Number(ateNumero),
      valorPago: Number(valorPago),
      data,
    }),
    onSuccess: () => { onAplicado(); toast.success('Pagamento antecipado aplicado.'); onClose() },
    onError: (e: Error) => setErro(e.message),
  })

  return (
    <Modal
      open onClose={onClose} title={`Pagamento antecipado · ${descricao}`}
      footer={<><Button variant="light" onClick={onClose}>Cancelar</Button><Button icon="ri-check-line" loading={adiantar.isPending} disabled={!ateNumero || !valorPago} onClick={() => adiantar.mutate()}>Confirmar</Button></>}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Da parcela nº" type="number" min={minNumero} max={maxNumero} value={desdeNumero} onChange={(e) => setDesdeNumero(e.target.value)} placeholder={String(minNumero ?? '')} />
          <Input label="Até a parcela nº" type="number" min={minNumero} max={maxNumero} value={ateNumero} onChange={(e) => setAteNumero(e.target.value)} />
        </div>
        <Input label="Valor pago (R$)" type="number" min={0} step="0.01" value={valorPago} onChange={(e) => setValorPago(e.target.value)} />
        <Input label="Data do pagamento" type="date" value={data} onChange={(e) => setData(e.target.value)} />
        {selecionadas.length > 0 && (
          <p className="text-2xs text-muted">
            {selecionadas.length} parcela(s) selecionada(s), somando {formatMoney(valorNominal)} nominal.
            {valorPago && <> Desconto: <strong className="text-income">{formatMoney(Math.max(valorNominal - Number(valorPago), 0))}</strong></>}
          </p>
        )}
        {erro && <p className="text-13 text-expense">{erro}</p>}
      </div>
    </Modal>
  )
}

function RenegociarModal({ recorrenciaId, valorParcelaAtual, valorRestante, quantidadePendente, onClose, onAplicado }: {
  recorrenciaId: string; valorParcelaAtual: number; valorRestante: number; quantidadePendente: number
  onClose: () => void; onAplicado: () => void
}) {
  const toast = useToast()
  const [modo, setModo] = useState<'parcela' | 'total'>('parcela')
  const [valorParcela, setValorParcela] = useState(String(valorParcelaAtual))
  const [valorTotal, setValorTotal] = useState(String(valorRestante))
  const [quantidade, setQuantidade] = useState(String(quantidadePendente))
  const [observacao, setObservacao] = useState('')
  const [erro, setErro] = useState<string | null>(null)

  const qtd = Number(quantidade) || 0
  const novoValorParcela = modo === 'parcela' ? Number(valorParcela) || 0 : qtd > 0 ? (Number(valorTotal) || 0) / qtd : 0
  const novoValorTotal = modo === 'total' ? Number(valorTotal) || 0 : (Number(valorParcela) || 0) * qtd
  const diferenca = novoValorTotal - valorRestante

  const renegociar = useMutation({
    mutationFn: () => recorrenciasApi.renegociar(recorrenciaId, {
      novoValorParcela, novaQuantidadeParcelas: qtd, observacao: observacao || undefined,
    }),
    onSuccess: () => { onAplicado(); toast.success('Recorrência renegociada.'); onClose() },
    onError: (e: Error) => setErro(e.message),
  })

  return (
    <Modal
      open onClose={onClose} title="Renegociar"
      footer={<><Button variant="light" onClick={onClose}>Cancelar</Button><Button icon="ri-check-line" loading={renegociar.isPending} disabled={qtd < 1 || novoValorParcela <= 0} onClick={() => renegociar.mutate()}>Confirmar renegociação</Button></>}
    >
      <div className="space-y-3">
        <p className="text-13 text-muted">
          Hoje: <strong className="text-white">{formatMoney(valorRestante)}</strong> restantes em <strong className="text-white">{quantidadePendente}</strong> parcela(s) pendente(s).
        </p>
        <div className="flex gap-2">
          <button type="button" onClick={() => setModo('parcela')} className={`h-9 flex-1 rounded border text-13 ${modo === 'parcela' ? 'border-primary bg-primary/10 text-primary' : 'border-border/70 text-muted'}`}>Valor por parcela</button>
          <button type="button" onClick={() => setModo('total')} className={`h-9 flex-1 rounded border text-13 ${modo === 'total' ? 'border-primary bg-primary/10 text-primary' : 'border-border/70 text-muted'}`}>Valor total</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {modo === 'parcela' ? (
            <Input label="Valor por parcela (R$)" type="number" min={0} step="0.01" value={valorParcela} onChange={(e) => setValorParcela(e.target.value)} autoFocus />
          ) : (
            <Input label="Valor total (R$)" type="number" min={0} step="0.01" value={valorTotal} onChange={(e) => setValorTotal(e.target.value)} autoFocus />
          )}
          <Input label="Quantidade de parcelas" type="number" min={1} max={600} step={1} value={quantidade} onChange={(e) => setQuantidade(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3 rounded border border-border/70 p-3 text-13">
          <div>
            <p className="text-2xs uppercase text-muted">Novo valor por parcela</p>
            <p className="font-semibold text-white">{formatMoney(novoValorParcela)}</p>
          </div>
          <div>
            <p className="text-2xs uppercase text-muted">Novo total</p>
            <p className="font-semibold text-white">{formatMoney(novoValorTotal)}</p>
          </div>
          <div className="col-span-2">
            <p className="text-2xs uppercase text-muted">Diferença em relação ao valor restante atual</p>
            <p className={`font-semibold ${diferenca > 0 ? 'text-expense' : diferenca < 0 ? 'text-income' : 'text-white'}`}>
              {diferenca > 0 ? '+' : ''}{formatMoney(diferenca)} {diferenca > 0 ? '(pagando a mais)' : diferenca < 0 ? '(pagando a menos)' : ''}
            </p>
          </div>
        </div>
        <Input label="Observação (opcional)" value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="Ex: acordo verbal em 10/2026" />
        {erro && <p className="text-13 text-expense">{erro}</p>}
      </div>
    </Modal>
  )
}

function HistoricoRenegociacoesModal({ recorrenciaId, onClose }: { recorrenciaId: string; onClose: () => void }) {
  const { data: itens = [], isLoading } = useQuery({
    queryKey: ['recorrencias', recorrenciaId, 'renegociacoes'],
    queryFn: () => recorrenciasApi.listarRenegociacoes(recorrenciaId),
  })

  return (
    <Modal open onClose={onClose} title="Histórico de renegociações">
      {isLoading ? (
        <LoadingState />
      ) : itens.length === 0 ? (
        <p className="text-13 text-muted">Nenhuma renegociação registrada.</p>
      ) : (
        <div className="space-y-3">
          {itens.map((h) => (
            <div key={h.id} className="rounded border border-border/70 p-3 text-13">
              <p className="text-2xs text-muted">{formatDate(h.data)}</p>
              <p className="text-white">
                {formatMoney(h.valorParcelaAntes)} <i className="ri-arrow-right-line text-muted" aria-hidden /> <strong>{formatMoney(h.valorParcelaDepois)}</strong> por parcela
              </p>
              <p className="text-white">
                {h.quantidadeParcelasAntes ?? '—'} <i className="ri-arrow-right-line text-muted" aria-hidden /> <strong>{h.quantidadeParcelasDepois}</strong> parcelas
              </p>
              {h.observacao && <p className="mt-1 text-muted">{h.observacao}</p>}
            </div>
          ))}
        </div>
      )}
    </Modal>
  )
}
